import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { serverTimestamp } from '@angular/fire/firestore';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { Group } from '../../../../models/group.model';
import { Member } from '../../../../models/member.model';
import { MembershipStatus } from '../../../../models/membership.status';
import * as memberActions from '../../../../state/actions/member.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';

@Component({
  selector: 'app-dlg-change-status',
  standalone: true,
  imports: [
    MatFormFieldModule,
    BtnActionComponent,
    AsyncPipe,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    BtnSubmitComponent,
  ],
  templateUrl: './dlg-change-status.component.html',
  styleUrl: './dlg-change-status.component.css',
})
export class DlgChangeStatusComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<DlgChangeStatusComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  private dialog: MatDialog = inject(MatDialog);
  private actions$: Actions = inject(Actions);
  logger: NGXLogger = inject(NGXLogger);

  title: string;
  description: string = 'Select an option to proceed';
  action: string = 'Update Status';
  options: MembershipStatus[] = [];
  selectedOption: MembershipStatus | null = null;

  working$: Observable<boolean> = of(false);
  exit$: Subject<void> = new Subject();

  group: Group | undefined;
  seatsLimit: number = 100;
  isSubscribed: boolean = false;

  constructor(private store: Store<AppState>) {
    this.title = this.data.title;
    this.description = this.data.description;
    this.action = this.data.action || 'Update Status';
    this.selectedOption = this.data.member?.status ?? null;

    this.options = [
      MembershipStatus.active,
      MembershipStatus.requested,
      MembershipStatus.invited,
      MembershipStatus.rejected,
      MembershipStatus.removed,
      MembershipStatus.alumni,
    ];
  }

  ngOnInit(): void {
    const groupId = this.data.member?.groupId;
    if (groupId) {
      this.store
        .select(groupSelector.getGroupById(groupId))
        .pipe(takeUntil(this.exit$))
        .subscribe((group) => {
          this.group = group;
        });

      const defaultFreeSeats = 100;
      this.store
        .select(groupSelector.getGroupMetadataMap)
        .pipe(takeUntil(this.exit$))
        .subscribe((metadataMap) => {
          const metadata = metadataMap ? metadataMap[groupId] : null;
          if (metadata) {
            this.seatsLimit = metadata.subscribedSeatsTier ?? defaultFreeSeats;
            this.isSubscribed = this.seatsLimit > defaultFreeSeats;
          } else {
            this.seatsLimit = defaultFreeSeats;
            this.isSubscribed = false;
          }
        });
    }

    this.actions$
      .pipe(ofType(memberActions.updateStatus), takeUntil(this.exit$))
      .subscribe(() => {
        this.working$ = of(true);
      });

    this.actions$
      .pipe(ofType(memberActions.updateStatusSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        this.logger.debug('Member status updated successfully');
        this.working$ = of(false);
        this.dialogRef.close(true);
      });

    this.actions$
      .pipe(ofType(memberActions.updateStatusFailure), takeUntil(this.exit$))
      .subscribe(({ error }) => {
        this.logger.debug('Member status update failed, error: ', error);
        this.onShowAlert(error);
        this.working$ = of(false);
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onChangeOption(event: any): void {
    const value: MembershipStatus = event.target.value;
    this.selectedOption = value;
  }

  onTextSelection(value: MembershipStatus) {
    this.selectedOption = value;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (
      this.selectedOption == null ||
      this.selectedOption === this.data.member.status
    ) {
      return this.onCancel();
    }

    if (this.selectedOption === MembershipStatus.removed) {
      this.store.dispatch(
        memberActions.deleteAction({
          id: this.data.member.id!,
        }),
      );
      this.dialogRef.close(true);
      return;
    }

    if (this.selectedOption === MembershipStatus.active) {
      const currentMembers = this.group?.activeMembers ?? 0;
      if (currentMembers >= this.seatsLimit) {
        let title = 'Free Tier Limit Reached';
        let message = 'Converting a requester to an active member requires upgrading to a paid plan. Please upgrade your plan in the Billing tab to add more members.';

        if (this.isSubscribed) {
          title = 'Plan Limit Reached';
          message = `Converting a requester to an active member exceeds your current plan limit of ${this.seatsLimit} seats. Please upgrade your plan in the Billing tab to add more members.`;
        }

        this.onShowAlert({
          title,
          message,
        } as AppExceptionModel);
        return;
      }
    }

    this.store.dispatch(
      memberActions.updateStatus({
        id: this.data.member.id!,
        member: {
          ...this.data.member,
          status: this.selectedOption,
        } as Member,
      }),
    );
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title || 'Error',
      description: error.message || 'An error occurred.',
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);
  }
}
